import { supabase } from "@/integrations/supabase/client";

type Operation = "create" | "update" | "delete";
type Channel = "instagram" | "tiktok" | "facebook" | "linkedin" | "threads" | "youtube";

interface ScheduleParams {
  userId: string;
  videoContentId: string;
  publicationId?: string;
  channel: Channel;
  scheduledAt: string;
  metricoolPostId?: string | null;
  isMultihook?: boolean;
}

interface ScheduleResult {
  success: boolean;
  error?: string;
  metricoolPostId?: string;
  scheduledAt?: string;
}

async function callMetricoolScheduler(
  operation: Operation,
  params: ScheduleParams
): Promise<ScheduleResult> {
  try {
    console.log(`[metricoolScheduler] ${operation} for ${params.channel}`, params);

    const { data, error } = await supabase.functions.invoke("metricool-scheduler", {
      body: {
        operation,
        user_id: params.userId,
        video_content_id: params.videoContentId,
        publication_id: params.publicationId,
        channel: params.channel,
        scheduled_at: params.scheduledAt,
        metricool_post_id: params.metricoolPostId,
        is_multihook: params.isMultihook
      }
    });

    if (error) {
      console.warn("[metricoolScheduler] Function error:", error);
      return { success: false, error: error.message };
    }

    if (!data?.success) {
      return { success: false, error: data?.error || "Unknown error" };
    }

    return {
      success: true,
      metricoolPostId: data.metricool_post_id,
      scheduledAt: data.scheduled_at
    };
  } catch (error) {
    console.warn("[metricoolScheduler] Error:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error"
    };
  }
}

/**
 * Schedule a new publication to Metricool
 */
export async function createMetricoolSchedule(params: ScheduleParams): Promise<ScheduleResult> {
  return callMetricoolScheduler("create", params);
}

/**
 * Update an existing scheduled publication in Metricool
 */
export async function updateMetricoolSchedule(params: ScheduleParams): Promise<ScheduleResult> {
  return callMetricoolScheduler("update", params);
}

/**
 * Cancel/delete a scheduled publication from Metricool
 */
export async function deleteMetricoolSchedule(params: ScheduleParams): Promise<ScheduleResult> {
  return callMetricoolScheduler("delete", params);
}

/**
 * Sync publications to Metricool - handles create, update, delete in batch
 */
export async function syncPublicationsToMetricool(
  userId: string,
  videoContentId: string,
  newPublications: Array<{ channel: Channel; scheduledAt: string }>,
  oldPublications: Array<{ id: string; channel: Channel; scheduled_at: string; metricool_post_id: string | null }>,
  currentDbPublications?: Array<{ id: string; channel: Channel; scheduled_at: string; metricool_post_id: string | null }>
): Promise<{ success: boolean; errors: string[] }> {
  const errors: string[] = [];

  // Build maps for comparison using OLD publications (before replace)
  const oldByChannel = new Map(
    oldPublications.map(p => [p.channel, p])
  );
  const newByChannel = new Map(
    newPublications.map(p => [p.channel, p])
  );

  // Map of current DB publication IDs by channel (after replace)
  const currentDbByChannel = new Map(
    (currentDbPublications || []).map(p => [p.channel, p])
  );

  // Find publications to delete (existed before but not in new list)
  const toDelete = oldPublications.filter(p => !newByChannel.has(p.channel));

  // Find publications to create (in new list but not in old)
  const toCreate = newPublications.filter(p => !oldByChannel.has(p.channel));

  // Find publications to update (in both, but may have different dates)
  const toUpdate = newPublications.filter(p => {
    const old = oldByChannel.get(p.channel);
    if (!old) return false;
    const oldDate = new Date(old.scheduled_at).getTime();
    const newDate = new Date(p.scheduledAt).getTime();
    return oldDate !== newDate;
  });

  console.log(`[syncPublicationsToMetricool] To create: ${toCreate.length}, To update: ${toUpdate.length}, To delete: ${toDelete.length}`);

  // Execute deletions (use old metricool_post_id)
  for (const pub of toDelete) {
    const result = await deleteMetricoolSchedule({
      userId,
      videoContentId,
      publicationId: pub.id,
      channel: pub.channel,
      scheduledAt: pub.scheduled_at,
      metricoolPostId: pub.metricool_post_id
    });
    if (!result.success) {
      errors.push(`Failed to delete ${pub.channel}: ${result.error}`);
    }
  }

  // Execute creates — pass the NEW DB publication ID so the edge function stores metricool_post_id on it
  for (const pub of toCreate) {
    const currentDbPub = currentDbByChannel.get(pub.channel);
    const result = await createMetricoolSchedule({
      userId,
      videoContentId,
      publicationId: currentDbPub?.id,
      channel: pub.channel,
      scheduledAt: pub.scheduledAt
    });
    if (!result.success) {
      errors.push(`Failed to create ${pub.channel}: ${result.error}`);
    }
  }

  // Execute updates — use old metricool_post_id but NEW DB publication ID
  for (const pub of toUpdate) {
    const old = oldByChannel.get(pub.channel);
    const currentDbPub = currentDbByChannel.get(pub.channel);
    const result = await updateMetricoolSchedule({
      userId,
      videoContentId,
      publicationId: currentDbPub?.id || old!.id,
      channel: pub.channel,
      scheduledAt: pub.scheduledAt,
      metricoolPostId: old!.metricool_post_id
    });
    if (!result.success) {
      errors.push(`Failed to update ${pub.channel}: ${result.error}`);
    }
  }

  return {
    success: errors.length === 0,
    errors
  };
}
